/**
 * Retouren v3 (sessie 172, ontwerp 90): lijst op aandacht, dossier als held.
 *
 * - Vanaf 1100 px: de lijst links (max 520 px), het dossier rechts. De
 *   lijst opent met de bovenste regel gekozen.
 * - Daaronder een stapel: eerst het lijstscherm, na een tik het
 *   dossierscherm. Het dossier tekent dan zelf zijn kop met terugknop en
 *   de vaste hoofdknop onderaan.
 * - De keuze staat in de url (`?retour=<id>`): een link met die parameter
 *   opent op de telefoon meteen het dossier.
 * - Geen copiloot, geen tellers: de copiloot blijft in de zaak.
 * - De melding na het aanmaken (route-state `retourMelding`, sessie 171)
 *   staat bovenaan tot JP hem sluit.
 */
import "@fontsource/source-sans-3/400.css";
import "@fontsource/source-sans-3/600.css";
import "@fontsource/source-sans-3/700.css";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { RetourV3Filter } from "@/lib/retouren-v3-api";
import type { RetourMeldingState } from "@/pages/Admin/RetourAanmaken";
import { retourLink } from "@/pages/Admin/retourLink";

import { RETOUREN_V3_LIJST_SLEUTEL, RetourenLijstV3 } from "./RetourenLijstV3";
import { RetourDossierV3 } from "./v3/RetourDossierV3";
import "./retouren-v3.css";
import "@/styles/statusbalk.css";

const BREED_VANAF_PX = 1100;

/** Vanaf 1100 px lijst en dossier naast elkaar; daaronder de stapel. */
function useBreed(): boolean {
  const vraag = `(min-width: ${BREED_VANAF_PX}px)`;
  const [breed, setBreed] = useState(
    () => typeof window !== "undefined" && window.matchMedia(vraag).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(vraag);
    const luister = (e: MediaQueryListEvent) => setBreed(e.matches);
    media.addEventListener("change", luister);
    return () => media.removeEventListener("change", luister);
  }, [vraag]);
  return breed;
}

export function WerkplekRetouren() {
  const breed = useBreed();
  const smal = !breed;
  const [params, setParams] = useSearchParams();
  const retourId = params.get("retour");
  const [filter, setFilter] = useState<RetourV3Filter>("aandacht");
  const [zoek, setZoek] = useState("");
  const qc = useQueryClient();

  const plek = useLocation();
  const navigeer = useNavigate();
  const melding = (plek.state as RetourMeldingState | null)?.retourMelding ?? null;
  const sluitMelding = () =>
    navigeer(`${plek.pathname}${plek.search}`, { replace: true, state: null });

  // Na een filter- of zoekwissel springt de keuze naar de bovenste als de
  // gekozen retour niet meer in de lijst staat. Een link naar een retour
  // buiten het filter blijft bij het openen gewoon staan.
  const gewisseld = useRef(false);
  const huidig = useRef({ retourId, breed });
  const zetParams = useRef(setParams);
  // Layout-effect: loopt vóór de effecten van de lijst, dus `opLijst` leest
  // al de url van deze render.
  useLayoutEffect(() => {
    huidig.current = { retourId, breed };
    zetParams.current = setParams;
  });

  const kiesStil = useCallback((id: string) => {
    zetParams.current(
      (vorige) => {
        const volgende = new URLSearchParams(vorige);
        volgende.set("retour", id);
        return volgende;
      },
      { replace: true },
    );
  }, []);

  const opLijst = useCallback(
    (ids: string[]) => {
      const { retourId: gekozen, breed: isBreed } = huidig.current;
      const wissel = gewisseld.current;
      gewisseld.current = false;
      if (!isBreed) return;
      const boven = ids[0];
      if (!boven) return;
      if (gekozen && (!wissel || ids.includes(gekozen))) return;
      if (gekozen === boven) return;
      kiesStil(boven);
    },
    [kiesStil],
  );

  const kies = (id: string) => {
    if (id === retourId) return;
    setParams(
      (vorige) => {
        const volgende = new URLSearchParams(vorige);
        volgende.set("retour", id);
        return volgende;
      },
      // Op de telefoon is het dossier een eigen scherm: terug in de
      // browser brengt je dan terug in de lijst.
      { replace: breed },
    );
  };

  const terug = () => {
    setParams(
      (vorige) => {
        const volgende = new URLSearchParams(vorige);
        volgende.delete("retour");
        return volgende;
      },
      { replace: false },
    );
  };

  const opFilter = (nieuw: RetourV3Filter) => {
    if (nieuw === filter) return;
    gewisseld.current = true;
    setFilter(nieuw);
  };
  const opZoek = (nieuw: string) => {
    gewisseld.current = true;
    setZoek(nieuw);
  };

  const opGewijzigd = () => {
    void qc.invalidateQueries({ queryKey: RETOUREN_V3_LIJST_SLEUTEL });
  };

  const nieuw = () => navigeer(retourLink({}));

  const lijst = (
    <RetourenLijstV3
      filter={filter}
      onFilter={opFilter}
      zoek={zoek}
      onZoek={opZoek}
      gekozenId={retourId}
      onKies={kies}
      onLijst={opLijst}
      onNieuw={nieuw}
      smal={smal}
    />
  );

  return (
    <div className="rv3" data-smal={smal ? "ja" : "nee"}>
      {melding ? (
        <div
          className="rv3-melding"
          role="status"
          data-testid="wp-retour-melding"
          data-soort={melding.soort}
        >
          <span>{melding.tekst}</span>
          <button
            type="button"
            className="rv3-knop rv3-knop-klein"
            aria-label="Melding sluiten"
            onClick={sluitMelding}
          >
            Sluiten
          </button>
        </div>
      ) : null}

      {breed ? (
        <div className="rv3-breed">
          <div className="rv3-lijstkolom">{lijst}</div>
          <div className="rv3-dossierkolom">
            {retourId ? (
              <RetourDossierV3
                key={retourId}
                retourId={retourId}
                smal={false}
                onGewijzigd={opGewijzigd}
              />
            ) : (
              <section aria-label="Retourdossier" className="rv3-leegvlak">
                Kies een retour in de lijst.
              </section>
            )}
          </div>
        </div>
      ) : (
        <div className="rv3-stapel">
          {retourId ? (
            <div className="rv3-dossierscherm">
              <RetourDossierV3
                key={retourId}
                retourId={retourId}
                smal
                onTerug={terug}
                onGewijzigd={opGewijzigd}
              />
            </div>
          ) : (
            lijst
          )}
        </div>
      )}
    </div>
  );
}
